<script setup lang="ts">
import type { Values } from '@/composables/useValues'

const props = defineProps<{
    data: Values<Import>
}>()

/**
 * Use the metadata.
 */
const { meta } = useMeta()

/**
 * Use params.
 */
const { config } = useParams()

/**
 * Whether any product is selected.
 */
const hasSelection = computed(() => {
    return config.importProducts?.length
})

/**
 * Get the total value for a product.
 */
function getTotalByProduct(id: number) {
    return props.data?.countryData?.products
        ?.filter(product => product.product_id === id)
        ?.reduce((total, { import_value }) => total + import_value, 0)
}

/**
 * Top 5 at risk products.
 */
const topFive = computed(() => {
    return meta.products
        .sort((a, b) => {
            const aTotal = getTotalByProduct(a.id) || 0
            const bTotal = getTotalByProduct(b.id) || 0

            return bTotal - aTotal
        })
        .slice(0, 5)
})

/**
 * Whether the product is selected.
 */
function isSelected(id: number) {
    return config.importProducts?.includes(id)
}

/**
 * Toggle the product selection.
 */
function toggleProduct(id: number) {
    if (isSelected(id)) {
        config.importProducts = config.importProducts?.filter(
            product => product !== id
        )
    } else {
        config.importProducts = [...(config.importProducts || []), id]
    }
}
</script>

<template>
    <div
        class="grid auto-cols-fr grid-flow-col auto-rows-fr gap-2 overflow-hidden px-2 screen-ratio-[2]:!grid-cols-2 xl:grid-flow-row xl:grid-cols-3"
    >
        <div
            v-for="product in topFive"
            :key="product.id"
            :style="{
                background: product.color,
                opacity: !hasSelection || isSelected(product.id) ? 1 : 0.5
            }"
            tabindex="0"
            class="cols-span-1 flex cursor-pointer flex-col justify-center gap-2 p-2 outline-none ring-black transition duration-300 ease-in-out hover:ring-1 focus:ring-1"
            @click="toggleProduct(product.id)"
            @keypress.space="toggleProduct(product.id)"
        >
            <div class="flex-1 overflow-hidden p-1">
                <img
                    :src="product.icon"
                    :alt="product.name"
                    class="mx-auto h-full max-h-24 object-contain brightness-0 invert"
                />
            </div>

            <p
                class="hidden flex-shrink-0 truncate text-center text-xs text-white 2xl:block"
            >
                {{ product.name }}
            </p>
        </div>
    </div>
</template>